import type { ComponentType } from 'react';
import type {
  ContentBlock,
  TextNode,
  HeadingBlock,
  ParagraphBlock,
  ListBlock,
  QuoteBlock,
  CalloutBlock,
  ImageBlock,
  GalleryBlock,
  VideoBlock,
  EmbedBlock,
  BlockType,
} from '@lightive/shared';

// =============================================================================
// URL SANITIZATION
// =============================================================================

/** Blocks dangerous URL schemes (javascript:, data:, vbscript:). */
function sanitizeImageUrl(url: string | undefined): string {
  if (!url) return '';
  const lower = url.toLowerCase().trim();
  if (
    lower.startsWith('https://') ||
    lower.startsWith('http://') ||
    lower.startsWith('/')
  ) {
    return url;
  }
  return '';
}

/** Sanitizes srcSet by validating each URL entry. */
function sanitizeSrcSet(srcSet: string | undefined): string | undefined {
  if (!srcSet) return undefined;
  return srcSet
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => {
      const url = entry.split(/\s+/)[0];
      return sanitizeImageUrl(url) !== '';
    })
    .join(', ') || undefined;
}

/** Validates a video ID contains only safe characters (alphanumeric, hyphens, underscores). */
function sanitizeVideoId(videoId: string | undefined): string {
  if (!videoId) return '';
  return /^[a-zA-Z0-9_-]+$/.test(videoId) ? videoId : '';
}

/** Sanitizes embed URL - only allows https:// for direct embeds. */
function sanitizeEmbedUrl(url: string | undefined): string {
  if (!url) return '';
  const lower = url.toLowerCase().trim();
  if (lower.startsWith('https://')) return url;
  return '';
}

// =============================================================================
// FACTORY PATTERN: Block Component Registry
// =============================================================================
// This registry maps block types to their React components.
// To add a new block type:
// 1. Create the component function below
// 2. Add it to the BLOCK_COMPONENT_REGISTRY
// =============================================================================

type BlockComponentProps<T extends ContentBlock = ContentBlock> = {
  block: T;
};

/**
 * Registry mapping block types to their rendering components.
 * Using object-map factory pattern for O(1) lookup and easy extensibility.
 */
const BLOCK_COMPONENT_REGISTRY: Record<
  BlockType,
  ComponentType<BlockComponentProps<ContentBlock>>
> = {
  heading: HeadingComponent as ComponentType<BlockComponentProps<ContentBlock>>,
  paragraph: ParagraphComponent as ComponentType<BlockComponentProps<ContentBlock>>,
  list: ListComponent as ComponentType<BlockComponentProps<ContentBlock>>,
  quote: QuoteComponent as ComponentType<BlockComponentProps<ContentBlock>>,
  callout: CalloutComponent as ComponentType<BlockComponentProps<ContentBlock>>,
  image: ImageComponent as ComponentType<BlockComponentProps<ContentBlock>>,
  gallery: GalleryComponent as ComponentType<BlockComponentProps<ContentBlock>>,
  video: VideoComponent as ComponentType<BlockComponentProps<ContentBlock>>,
  embed: EmbedComponent as ComponentType<BlockComponentProps<ContentBlock>>,
};

/**
 * Factory function to get the appropriate component for a block type.
 * Returns null component if block type is not registered.
 */
function getBlockComponent(
  type: BlockType
): ComponentType<BlockComponentProps<ContentBlock>> | null {
  return BLOCK_COMPONENT_REGISTRY[type] ?? null;
}

// =============================================================================
// PUBLIC API
// =============================================================================

interface BlockRendererProps {
  blocks: ContentBlock[];
  /** Optional class name for the container */
  className?: string;
}

/**
 * Renders an array of content blocks using the registered block components.
 * Uses the Factory Pattern with a component registry for extensibility.
 */
export function BlockRenderer({ blocks, className = '' }: BlockRendererProps) {
  if (!blocks || blocks.length === 0) {
    return null;
  }

  return (
    <div className={`content-blocks space-y-6 ${className}`.trim()}>
      {blocks.map((block) => (
        <BlockComponent key={block.id} block={block} />
      ))}
    </div>
  );
}

/**
 * Renders a single content block by looking up its component in the registry.
 */
function BlockComponent({ block }: { block: ContentBlock }) {
  const Component = getBlockComponent(block.type);

  if (!Component) {
    // Log warning in development for unknown block types
    if (import.meta.env.DEV) {
      console.warn(`BlockRenderer: Unknown block type "${block.type}"`);
    }
    return null;
  }

  return <Component block={block} />;
}

function HeadingComponent({ block }: { block: HeadingBlock }) {
  const Tag = `h${block.attrs.level}` as 'h2' | 'h3';
  const className =
    block.attrs.level === 2
      ? 'font-en font-bold text-3xl md:text-4xl uppercase not-italic text-text mb-4'
      : 'font-en font-semibold text-xl md:text-2xl uppercase not-italic text-text mb-3';

  return (
    <Tag className={className}>
      <TextContent content={block.content} />
    </Tag>
  );
}

function ParagraphComponent({ block }: { block: ParagraphBlock }) {
  if (!block.content || block.content.length === 0) {
    return <div className="h-4" />;
  }

  return (
    <p className="font-kr leading-relaxed text-base md:text-lg text-white/80">
      <TextContent content={block.content} />
    </p>
  );
}

function ListComponent({ block }: { block: ListBlock }) {
  const Tag = block.attrs.style === 'ordered' ? 'ol' : 'ul';
  const listClass =
    block.attrs.style === 'ordered'
      ? 'list-decimal list-inside space-y-2'
      : 'list-disc list-inside space-y-2';

  return (
    <Tag className={`${listClass} font-kr text-text`}>
      {block.content.map((item, index) => (
        <li key={`list-item-${index}-${item.content.map((n) => n.text).join('')}`} className="leading-relaxed">
          <TextContent content={item.content} />
        </li>
      ))}
    </Tag>
  );
}

function QuoteComponent({ block }: { block: QuoteBlock }) {
  return (
    <blockquote className="border-l border-[var(--color-rule)] pl-6 py-2 my-6">
      <p className="font-kr text-text/80 text-lg italic leading-relaxed">
        <TextContent content={block.content} />
      </p>
      {block.attrs?.citation && (
        <cite className="block mt-3 font-en text-sm text-text/60 not-italic">
          - {block.attrs.citation}
        </cite>
      )}
    </blockquote>
  );
}

function CalloutComponent({ block }: { block: CalloutBlock }) {
  return (
    <aside className="bg-highlight/30 rounded-xl p-5 border-l-4 border-highlight my-6">
      <p className="font-kr text-text leading-relaxed">
        <TextContent content={block.content} />
      </p>
    </aside>
  );
}

function ImageComponent({ block }: { block: ImageBlock }) {
  const safeSrc = sanitizeImageUrl(block.attrs.src);
  const safeSrcSet = sanitizeSrcSet(block.attrs.srcSet);

  return (
    <figure className="my-8">
      {safeSrcSet ? (
        <img
          srcSet={safeSrcSet}
          sizes={block.attrs.sizes || '(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 1920px'}
          src={safeSrc}
          alt={block.attrs.alt}
          width={block.attrs.width}
          height={block.attrs.height}
          className="w-full rounded-xl shadow-lg"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <img
          src={safeSrc}
          alt={block.attrs.alt}
          width={block.attrs.width}
          height={block.attrs.height}
          className="w-full rounded-xl shadow-lg"
          loading="lazy"
        />
      )}
      {block.attrs.caption && (
        <figcaption className="mt-3 text-center font-kr text-sm text-text/60">
          {block.attrs.caption}
        </figcaption>
      )}
    </figure>
  );
}

function GalleryComponent({ block }: { block: GalleryBlock }) {
  const columns = block.attrs?.columns || 3;
  const gridCols = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  };

  const gallerySizes: Record<number, string> = {
    2: '(max-width: 768px) 100vw, 50vw',
    3: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
    4: '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw',
  };

  return (
    <div className={`grid ${gridCols[columns]} gap-4 my-8`}>
      {block.content.map((img) => {
        const safeSrc = sanitizeImageUrl(img.attrs.src);
        const safeSrcSet = sanitizeSrcSet(img.attrs.srcSet);

        return (
          <div key={img.id} className="aspect-square overflow-hidden rounded-xl">
            {safeSrcSet ? (
              <img
                srcSet={safeSrcSet}
                sizes={img.attrs.sizes || gallerySizes[columns]}
                src={safeSrc}
                alt={img.attrs.alt}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <img
                src={safeSrc}
                alt={img.attrs.alt}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function VideoComponent({ block }: { block: VideoBlock }) {
  const videoId = sanitizeVideoId(block.attrs.videoId);
  if (!videoId) return null;

  const embedUrl =
    block.attrs.provider === 'youtube'
      ? `https://www.youtube-nocookie.com/embed/${videoId}`
      : `https://player.vimeo.com/video/${videoId}`;

  return (
    <div className="aspect-video my-8 rounded-xl overflow-hidden shadow-lg">
      <iframe
        src={embedUrl}
        title={block.attrs.title || 'Video'}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

function EmbedComponent({ block }: { block: EmbedBlock }) {
  const getEmbedUrl = () => {
    const videoId = sanitizeVideoId(block.attrs.videoId);
    if (block.attrs.provider === 'youtube' && videoId) {
      return `https://www.youtube-nocookie.com/embed/${videoId}`;
    }
    if (block.attrs.provider === 'vimeo' && videoId) {
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return sanitizeEmbedUrl(block.attrs.src);
  };

  const embedUrl = getEmbedUrl();
  if (!embedUrl) return null;

  const isDirect = block.attrs.provider !== 'youtube' && block.attrs.provider !== 'vimeo';

  return (
    <div className="aspect-video my-8 rounded-xl overflow-hidden shadow-lg">
      <iframe
        src={embedUrl}
        title={block.attrs.title || 'Embedded content'}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        {...(isDirect && { sandbox: 'allow-scripts allow-same-origin' })}
      />
    </div>
  );
}

// =============================================================================
// STRATEGY PATTERN: Mark Renderers
// =============================================================================
// Each mark type has a strategy for wrapping content.
// To add a new mark type:
// 1. Add a renderer function to MARK_RENDERERS
// =============================================================================

type MarkRenderer = (
  element: React.ReactNode,
  mark: { type: string; attrs?: { href?: string } },
  key: string
) => React.ReactNode;

/**
 * Registry of mark rendering strategies.
 */
const MARK_RENDERERS: Record<string, MarkRenderer> = {
  bold: (element, _, key) => <strong key={key}>{element}</strong>,

  italic: (element, _, key) => <em key={key}>{element}</em>,

  highlight: (element, _, key) => (
    <mark key={key} className="bg-highlight px-1 rounded">
      {element}
    </mark>
  ),

  link: (element, mark, key) => {
    const href = sanitizeImageUrl(mark.attrs?.href);
    if (!href) return <span key={key}>{element}</span>;
    const isExternal = href.startsWith('http');
    return (
      <a
        key={key}
        href={href}
        className="text-accent hover:underline"
        {...(isExternal && {
          target: '_blank',
          rel: 'noopener noreferrer',
        })}
      >
        {element}
      </a>
    );
  },
};

/**
 * Applies a mark to an element using the registered strategy.
 */
function applyMark(
  element: React.ReactNode,
  mark: { type: string; attrs?: { href?: string } },
  key: string
): React.ReactNode {
  const renderer = MARK_RENDERERS[mark.type];
  return renderer ? renderer(element, mark, key) : element;
}

/**
 * Renders text nodes with their marks applied.
 * Uses Strategy Pattern for mark rendering.
 */
function TextContent({ content }: { content: TextNode[] }) {
  if (!content || content.length === 0) {
    return null;
  }

  return (
    <>
      {content.map((node, i) => {
        let element: React.ReactNode = node.text;

        if (node.marks && node.marks.length > 0) {
          for (const mark of node.marks) {
            element = applyMark(element, mark, `${mark.type}-${i}`);
          }
        }

        return <span key={`text-node-${i}-${node.text}`}>{element}</span>;
      })}
    </>
  );
}
